<template>
  <div class="p-drawer">
    <!-- アイコン部 -->
    <div class="p-drawer__icons">
      <a
        :class="{ 'p-drawer__icons__expand': true, 'is-disable': isSidebarWidthMax }"
        @click="expand"
      >
        <v-icon
          icon="mdi-play-circle"
          size="large"
        />
      </a>
      <a
        :class="{ 'p-drawer__icons__shrink': true, 'is-disable': isSidebarWidthMin }"
        @click="shrink"
      >
        <v-icon
          icon="mdi-play-circle"
          size="large"
        />
      </a>
    </div>

    <!-- サイドバー本体部 -->
    <v-navigation-drawer
      class="p-drawer__main"
      :width="sidebarWidth"
      location="right"
      permanent
    >
      <!-- 最小化時の表示 -->
      <a
        v-if="isSidebarWidthMin"
        style="display:block; height: 100%; width: 100%;"
        @click="expand"
      >
        <v-icon class="pt-4 pl-1">search</v-icon>
      </a>
      <div v-show="!isSidebarWidthMin">
        <!-- タブ一覧 -->
        <!-- タブ一覧 -->
        <v-tabs
          v-model="selectTabId"
          :height="40"
          bg-color="grey-lighten-3"
          :show-arrows="false"
          class="p-drawer__tabs"
        >
          <v-tab
            :key="SIDEBAR_TAB_IDS.USER_INFO"
            class="ma-0"
            :height="40"
            :value="SIDEBAR_TAB_IDS.USER_INFO"
            title="ユーザー情報"
          >
            <v-icon class="mr-1">
              mdi-account-outline
            </v-icon>
            <span class="font-weight-bold p-drawer__tab-label">ユーザー情報</span>
          </v-tab>
          <v-tab
            v-if="showOtherTabs"
            :key="SIDEBAR_TAB_IDS.SIMILAR_SEARCH"
            class="ma-0"
            :height="40"
            :value="SIDEBAR_TAB_IDS.SIMILAR_SEARCH"
            title="過去事例検索"
          >
            <v-icon>mdi-magnify</v-icon>
            <span class="font-weight-bold p-drawer__tab-label">過去事例検索</span>
          </v-tab>
          <v-tab
            v-if="showOtherTabs"
            :key="SIDEBAR_TAB_IDS.AI_CORRECTION"
            class="ma-0"
            :height="40"
            :value="SIDEBAR_TAB_IDS.AI_CORRECTION"
            title="AI添削"
          >
            <RobotIcon class="svg-correctionBlue-fill" />
            <span class="font-weight-bold p-drawer__tab-label">AI添削</span>
          </v-tab>
          <v-tab
            v-if="showOtherTabs"
            :key="SIDEBAR_TAB_IDS.JOURNAL"
            class="ma-0"
            :height="40"
            :value="SIDEBAR_TAB_IDS.JOURNAL"
            title="ジャーナル検索"
          >
            <v-icon class="mr-1">
              mdi-book-open-page-variant
            </v-icon>
            <span class="font-weight-bold p-drawer__tab-label">ジャーナル検索</span>
          </v-tab>
        </v-tabs>

        <!-- タブごとの表示切り替え -->
        <div
          class="bg-confirmLightBlue"
          style="min-height: calc(100vh - 48px - 64px - 40px);"
        >
          <AnytimeConsultationUserInfoTab
            v-show="selectTabId === SIDEBAR_TAB_IDS.USER_INFO"
            ref="userInfoTabRef"
            :consultation="anytimeConsultation || {}"
            :answer-editable="answerEditable"
          /> <!-- タブ「ユーザー情報」 -->
          <AnytimeConsultationSimilarSearchPanel
            v-if="showOtherTabs"
            v-show="selectTabId === SIDEBAR_TAB_IDS.SIMILAR_SEARCH"
            ref="anytimeConsultationSimilarSearchPanelRef"
          /> <!-- タブ「過去事例検索」 -->
          <AnytimeConsultationCorrectionTab
            v-if="showOtherTabs"
            v-show="selectTabId === SIDEBAR_TAB_IDS.AI_CORRECTION"
            ref="anytimeConsultationCorrectionTab"
          /> <!-- タブ「回答文添削」 -->
          <journal-tab 
            v-if="showOtherTabs"
            v-show="selectTabId === SIDEBAR_TAB_IDS.JOURNAL"
            ref="journalTabRef"
            type="anytimeConsultation"
          /> <!-- タブ「ジャーナル検索」 -->
        </div>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";

// 検索
import AnytimeConsultationSimilarSearchPanel from "@/vue/widgets/anytime-consultation-similar-search/ui/SearchTabPanel.vue";

// 添削
import AnytimeConsultationCorrectionTab from "@/vue/widgets/anytime-consultation-correction/ui/AnytimeConsultationCorrectionTab.vue";
import RobotIcon from "@/vue/components/images/robotIcon.vue";

// ジャーナル検索
import JournalTab from "@/vue/components/shared/sidebar/Journal/journalTab.vue";

// ユーザー情報（新規・読み取り専用）
import AnytimeConsultationUserInfoTab from "./AnytimeConsultationUserInfoTab.vue";

import { SIDEBAR_TAB_IDS } from "@/vue/features/anytime-consultation-sidebar/model/sidebarTabIds";
import { useSidebarRecordSync } from "@/vue/composables/useSidebarRecordSync";

const props = defineProps({
  showOtherTabs: { type: Boolean, default: true },
});

const store = useStore();
const route = useRoute();

// JournalTabコンポーネントの参照
const journalTabRef = ref(null);
// AnytimeConsultationSimilarSearchPanelコンポーネントの参照
const anytimeConsultationSimilarSearchPanelRef = ref(null);
// AnytimeConsultationUserInfoTabコンポーネントの参照
const userInfoTabRef = ref(null);

const sidebarTabId = computed(() => store.getters.sidebarTabId);
const sidebarWidthRank = computed(() => store.getters.sidebarWidthRank);
const sidebarWidth = computed(() => store.getters.sidebarWidth);
const isSidebarWidthMin = computed(() => store.getters.isSidebarWidthMin);
const isSidebarWidthMax = computed(() => store.getters.isSidebarWidthMax);
const anytimeConsultation = computed(() => store.getters.anytimeConsultation);
// 特記事項は回答の完了状態（送信予約済み等）に関係なく、担当者または担当外でも回答可能な権限の相談員のみ編集可能にする
const answerEditable = computed(() => store.getters.anytimeConsultationAnswer?.editableByDoctor === true);

const selectTabId = computed({
  get: () => (props.showOtherTabs ? sidebarTabId.value : SIDEBAR_TAB_IDS.USER_INFO),
  set: (val) => store.dispatch("selectSidebarTabId", val),
});

const expand = () => {
  if (!isSidebarWidthMax.value) {
    store.dispatch("updateSidebarWidthRank", sidebarWidthRank.value + 1);
  }
};

const shrink = () => {
  if (!isSidebarWidthMin.value) {
    store.dispatch("updateSidebarWidthRank", sidebarWidthRank.value - 1);
  }
};

const normalizeConsultationId = (value) => {
  const id = Number(value);
  return Number.isNaN(id) ? null : id;
};

const { initializeTabs } = useSidebarRecordSync({
  recordRef: anytimeConsultation,
  getTargetId: () => normalizeConsultationId(route.params.id),
  getRecordId: (c) => Number(c.id),
  tabIdRef: selectTabId,
  defaultTabId: SIDEBAR_TAB_IDS.USER_INFO,
  subTabRefs: [anytimeConsultationSimilarSearchPanelRef, journalTabRef],
  onRecordChange: () => store.dispatch("updateSidebarWidthRank", 2),
});

// showOtherTabsがfalse→trueに変わると過去事例検索/ジャーナル検索タブがv-ifで再マウントされ、
// 再マウント後のコンポーネントには start() が呼ばれていない状態になるため、再マウント完了後に呼び直す。
watch(() => props.showOtherTabs, (visible) => {
  if (!visible) return;
  nextTick(() => initializeTabs());
});

defineExpose({
  initializeTabs,
});
</script>

<style lang="scss" scoped>
.p-drawer {
  &__icons {
    position: absolute;
    z-index: 2000;
    /* v-navigation-drawerになぜか動的にz-indexが指定される */
    margin-left: -12px;
    display: flex;
    flex-direction: column;
    margin-top: 50px;

    &__expand {
      transform: rotate(180deg);
      height: 25px;

      &.is-disable {
        opacity: 0;
      }
    }

    &__shrink {
      height: 25px;
      margin-top: 8px;

      &.is-disable {
        opacity: 0;
      }
    }
  }

  &__main {
    border-left: 1px solid #C9C5C5;
    height: 100% !important;
    overflow: auto;
  }

  :deep(.v-navigation-drawer) {
    /* absoluteがつくとflexでうまく配置されない */
    position: initial !important;
  }
}

:deep(.custom-tabs .v-tab) {
  background-color: #F5F5F5;
}

// サイドバーを開閉した直後や画面幅が狭い場合にタブが横スクロール(見切れ)しないよう、
// 各タブを均等に縮小可能にし、ラベルを省略表示(...)にする。
:deep(.p-drawer__tabs .v-slide-group__content) {
  flex: 1 1 auto;
}

:deep(.p-drawer__tabs .v-tab) {
  flex: 0 1 auto;
  min-width: 0;
  max-width: none;
  padding: 0 8px;
}

:deep(.p-drawer__tabs .v-tab .v-btn__content) {
  min-width: 0;
  overflow: hidden;
}

.p-drawer__tab-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
</style>